import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, ShieldAlert } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { TextField, PrimaryButton } from "@/components/app/TextField";
import { useAuth } from "@/context/AuthContext";
import { authApi, apiError } from "@/lib/api";
import { ACCOUNT } from "@/constants/testIds";

function Section({ title, children }) {
  return (
    <section className="rounded-card border border-brand-line bg-brand-surface p-6">
      <h2 className="font-display text-lg font-semibold text-brand-text">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function Account() {
  const { user, entitlements, emailVerified, refresh, logoutAll } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.first_name || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await authApi.updateMe({ first_name: name });
      await refresh();
      toast.success("Zapisano zmiany.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Nie udało się zapisać."));
    } finally {
      setSaving(false);
    }
  };

  const resend = async () => {
    try {
      await authApi.resendVerification(user.email);
      toast.success("Wysłaliśmy nową wiadomość weryfikacyjną.");
    } catch {
      toast.error("Nie udało się wysłać wiadomości.");
    }
  };

  const doLogoutAll = async () => {
    await logoutAll();
    navigate("/logowanie", { replace: true });
  };

  const [pwCur, setPwCur] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConf, setPwConf] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const changePassword = async () => {
    if (pwNew.length < 8) { toast.error("Nowe hasło musi mieć min. 8 znaków."); return; }
    if (pwNew !== pwConf) { toast.error("Nowe hasła nie są identyczne."); return; }
    if (pwNew === pwCur) { toast.error("Nowe hasło musi różnić się od obecnego."); return; }
    setPwSaving(true);
    try {
      await authApi.changePassword(pwCur, pwNew);
      toast.success("Hasło zmienione. Zaloguj się ponownie.");
      setPwCur(""); setPwNew(""); setPwConf("");
      setTimeout(() => navigate("/logowanie", { replace: true }), 1200);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Nie udało się zmienić hasła."));
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <AppShell>
      <div data-testid={ACCOUNT.root} className="max-w-2xl">
        <header className="mb-8">
          <h1 className="font-display text-3xl font-semibold text-brand-text sm:text-4xl">Konto</h1>
          <p className="mt-2 text-brand-sec">Zarządzaj danymi profilu i dostępem.</p>
        </header>

        <div className="space-y-5">
          <Section title="Dane profilu">
            <div className="space-y-4">
              <div>
                <span className="mb-1.5 block text-sm font-medium text-brand-sec">E-mail</span>
                <p data-testid={ACCOUNT.email} className="rounded-field border border-brand-line bg-brand-bg px-4 py-3 text-[15px] text-brand-text">
                  {user?.email}
                </p>
              </div>
              <TextField
                label="Imię"
                testId={ACCOUNT.nameInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Twoje imię"
              />
              <div className="w-full sm:w-48">
                <PrimaryButton type="button" data-testid={ACCOUNT.saveButton} onClick={save} disabled={saving}>
                  {saving ? "Zapisywanie…" : "Zapisz zmiany"}
                </PrimaryButton>
              </div>
            </div>
          </Section>

          <Section title="E-mail i bezpieczeństwo">
            <div data-testid={ACCOUNT.verifyStatus} className="flex items-center gap-3">
              {emailVerified ? (
                <>
                  <BadgeCheck size={20} className="text-brand-accent" />
                  <span className="text-sm text-brand-sec">Adres e-mail potwierdzony.</span>
                </>
              ) : (
                <>
                  <ShieldAlert size={20} className="text-amber-400" />
                  <span className="text-sm text-brand-sec">Adres e-mail niepotwierdzony.</span>
                  <button type="button" onClick={resend} className="text-sm font-medium text-brand-accent hover:underline">
                    Wyślij link
                  </button>
                </>
              )}
            </div>
          </Section>

          <Section title="Zmień hasło">
            <div className="space-y-4">
              <p className="text-sm text-brand-sec">Po zmianie hasła wylogujemy Cię ze wszystkich urządzeń — zaloguj się ponownie nowym hasłem.</p>
              <TextField label="Obecne hasło" type="password" testId={ACCOUNT.pwCurrent} value={pwCur} onChange={(e) => setPwCur(e.target.value)} placeholder="Twoje aktualne hasło" />
              <TextField label="Nowe hasło" type="password" testId={ACCOUNT.pwNew} value={pwNew} onChange={(e) => setPwNew(e.target.value)} placeholder="Min. 8 znaków" />
              <TextField label="Powtórz nowe hasło" type="password" testId={ACCOUNT.pwConfirm} value={pwConf} onChange={(e) => setPwConf(e.target.value)} placeholder="Powtórz nowe hasło" />
              <div className="w-full sm:w-48">
                <PrimaryButton type="button" data-testid={ACCOUNT.pwSubmit} onClick={changePassword} disabled={pwSaving || !pwCur || !pwNew || !pwConf}>
                  {pwSaving ? "Zmienianie…" : "Zmień hasło"}
                </PrimaryButton>
              </div>
            </div>
          </Section>

          <Section title="Zakupione produkty">
            {entitlements.length === 0 ? (
              <p data-testid={ACCOUNT.noAccess} className="text-sm text-brand-sec">
                Brak wykupionego pakietu. Po zakupie pakiet pojawi się tutaj i w bibliotece.
              </p>
            ) : (
              <ul data-testid={ACCOUNT.entitlements} className="space-y-2">
                {entitlements.map((e, i) => (
                  <li key={i} className="flex items-center justify-between rounded-field border border-brand-line bg-brand-bg px-4 py-3">
                    <span className="font-medium text-brand-text">
                      {e.scope_type === "FULL" ? "Pełny katalog" : e.scope_id}
                    </span>
                    <span className="rounded-full bg-brand-accent/15 px-3 py-1 text-xs font-semibold text-brand-accent">
                      {e.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Sesje">
            <button
              type="button"
              data-testid={ACCOUNT.logoutAllButton}
              onClick={doLogoutAll}
              className="rounded-field border border-brand-line px-4 py-2.5 text-sm font-medium text-brand-sec transition-colors hover:text-brand-text"
            >
              Wyloguj ze wszystkich urządzeń
            </button>
          </Section>
        </div>
      </div>
    </AppShell>
  );
}
